// CustomersSmart.jsx — Smart customer directory: filters, WhatsApp quick actions, per-customer insights.
// Supports MULTIPLE classification methodologies (Saudi Local + RFM Global).
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { normPhone } from "./lib/export";
import { api } from "./lib/api";
import { startServiceConversation } from "./lib/startServiceConversation";
import BlastCampaignModal from "./BlastCampaignModal";
import { METHODOLOGIES, buildSegments, daysSince as segDaysSince, avgPerVisit as segAvg, getMethodology, getSegmentMeta } from "./lib/segmentation";
import { buildQuickWins, PRIORITY_META } from "./lib/rfmActions";
import SaudiMarketInsights from "./SaudiMarketInsights";
import AICampaignRecommender from "./AICampaignRecommender";
import { useCustomerFilter } from "./customer_intelligence/CustomerFilterContext";
import SegmentsLoadGate from "./components/SegmentsLoadGate";
import { zVar } from "./lib/zIndex";
import { InfoTip } from "./components/InfoTip";
import CustomerListModal from "./components/CustomerListModal";
import { CustomerCardModal } from "./inbox/CustomerCard";

// CS-1 (بند ج2) — حُذف بانيء روابط wa.me: كل مخاطبة عميل تمرّ الآن بمسار
// «محادثة جديدة» داخل المنصة (بوابة wa_gate + قالب معتمد + تسجيل المصدر).

const WA_TEMPLATES = [
  { key: "missed",  label: "💔 رسالة استرجاع", body: "مرحباً {name}! وحشتنا في ثواني ☕ آخر زيارة لك كانت قبل فترة — عندنا قهوة ساخنة وعرض حلو ينتظرك. متى تجي؟" },
  { key: "thanks",  label: "🙏 رسالة شكر",    body: "أهلاً {name} 🌹 شكراً إنك جزء من عائلة ثواني. زيارتك تسعدنا — قهوتك القادمة على حسابنا!" },
  { key: "offer",   label: "🎁 عرض جديد",      body: "مرحباً {name}! عندنا عرض جديد في ثواني تحبه — تعال جربه وشاركنا رأيك ☕" },
  { key: "welcome", label: "👋 ترحيب",          body: "أهلاً {name}! يسعدنا انضمامك لعائلة ثواني ☕ قهوتك القادمة بانتظارك — نشرّفنا بزيارتك مرة ثانية." },
];

const SEGMENTS_NOT_USED_KEPT_FOR_REFERENCE = [];

function avgPerVisit(c) { return segAvg(c); }

function daysSince(dateIso) {
  if (!dateIso) return Infinity;
  const t = Date.parse(dateIso);
  if (isNaN(t)) return Infinity;
  return Math.floor((Date.now() - t) / 86400000);
}

function statusBadge(c) {
  // A3 (البند 3أ) — بلا تصنيف محلي بعد اليوم: الشريحة تصل جاهزة في c.segment
  // من الخادم (classify_population). هذا يضمن تطابقها دائماً مع فلاتر
  // الشرائح في segmentation.js (كلاهما يقرأ نفس الحقل الآن، لا دالتين).
  const meta = getSegmentMeta(c.segment);
  // Strip the leading emoji (kept inside meta.label) — the row already shows
  // a colored chip, no need to duplicate the emoji.
  const lbl = (meta?.label || "عادي").replace(/^[^\u0600-\u06FF\u0750-\u077F]+\s*/, "");
  return { lbl, color: meta?.color || "var(--brown)" };
}

function relativeAr(dateIso) {
  if (!dateIso) return "—";
  const d = daysSince(dateIso);
  if (d === Infinity) return "—";
  if (d === 0) return "اليوم";
  if (d === 1) return "أمس";
  if (d < 7) return `قبل ${d} أيام`;
  if (d < 30) return `قبل ${Math.floor(d / 7)} أسابيع`;
  if (d < 365) return `قبل ${Math.floor(d / 30)} شهر`;
  return `قبل ${Math.floor(d / 365)} سنة`;
}

function fm(n) { return Math.round(Number(n) || 0).toLocaleString("en-US"); }

// Initials avatar (RTL-safe). First word's first letter.
function avatarLetter(name) {
  const t = (name || "").trim();
  if (!t) return "?";
  return t.charAt(0);
}

// Deterministic color from name (8-color palette)
const AVATAR_COLORS = ["#6B4E3D", "#C2A97F", "#2E7D32", "#D84315", "#1E6390", "#0F766E", "#9D174D", "#8B5CF6"];
function avatarColor(name) {
  const t = (name || "").trim();
  if (!t) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export default function CustomersSmart({ customers: customersFromArchive, spendingEnabled, campaigns = [], showMarketInsights = true, showRecommender = true }) {
  const [methodologyId, setMethodologyId] = useState(() => "saudi_pro");
  const [activeSegment, setActiveSegment] = useState("all");
  const [search, setSearch] = useState(() => {
    try { return new URL(window.location.href).searchParams.get("cq") || ""; }
    catch { return ""; }
  });
  const [selected, setSelected] = useState(null);
  const [profilePhoneKey, setProfilePhoneKey] = useState(null);
  const [waOpenFor, setWaOpenFor] = useState(null);
  const [smartAction, setSmartAction] = useState(null); // {customer, mode:'schedule'|'automate'}
  const [blastSegment, setBlastSegment] = useState(null);
  const [blastRec, setBlastRec] = useState(null);
  // B1 (2026-10) — «عدد العملاء» في لقطة الشريحة قابل للضغط الآن (كان ثابتاً).
  const [dirListModal, setDirListModal] = useState(null); // {code}

  // v200.47 (2026-08-13) — Live pos_sales directory replaces the archive
  // list. On mount, fetch /api/customers/directory (window=90) and use
  // its rows for every downstream computation. Root cause of the Aug 13
  // «آخر زيارة قبل شهر» leak: the screen consumed datasets.cust which
  // hasn't been updated since manual upload. Now every number here is
  // live pos_sales + classify_rfm.
  const [directory, setDirectory] = useState({ status: "loading", data: null });
  // الشريط المشترك: الفرع المختار (قابل للدمج مع بطاقات أخرى في هذا الملف).
  const segShared = useCustomerFilter();
  const segBranch = segShared?.branchId || "";
  // Phase 7c: نافذة تحليلية ثابتة 90 يومًا — قاعدة العملاء لا تُقلَّص بنافذة
  // الشريط المشترك الضيقة (قرار المالك: المسار 1a). غير موصولة بتاريخ الشريط.
  // 2026-08 (إصلاح دليل العملاء): الفرع أصبح مدعومًا فعليًا بالخلفية
  // (customers_directory.py) — كل رقم موصول بالفرع المختار، لا القاعدية فقط.
  useEffect(() => {
    let alive = true;
    const params = { window: 90 };
    if (segBranch) params.branch_id = segBranch;
    api.get("/customers/directory", { params })
      .then(({ data }) => alive && setDirectory({ status: "ok", data }))
      .catch(() => alive && setDirectory({ status: "error", data: null }));
    return () => { alive = false; };
  }, [segBranch]);

  const customers = useMemo(() => {
    if (directory.status !== "ok" || !directory.data) return [];
    return (directory.data.customers || []).map((c) => ({
      phone:       c.phone,
      phone_display: c.phone_display || c.phone,
      phone_key:   c.phone_key,
      name:        c.name || "",
      visits:      c.visits,
      totalSpent:  c.spent,
      paid:        c.visits,   // best-effort; visits count as paid
      free:        0,
      lastDate:    c.last_visit,
      firstDate:   c.first_visit,
      segment:     c.segment,
      aov:         c.aov,
      recency_days: c.recency_days,
      _source:     "pos_sales",
    }));
  }, [directory]);
  const isDirectoryLive = directory.status === "ok";
  const archiveMeta = directory.data?.archive || null;

  // Build segments dynamically per chosen methodology
  const SEGMENTS = useMemo(() => buildSegments(methodologyId, customers), [methodologyId, customers]);
  const activeMethod = useMemo(() => METHODOLOGIES.find((m) => m.id === methodologyId) || METHODOLOGIES[0], [methodologyId]);

  // v200.47 — Smart Quick-Wins fed from the SAME live endpoint as the
  // segment strip so their numbers match by construction. Fallback to
  // the old local computation only until the endpoint loads.
  const quickWins = useMemo(() => {
    if (isDirectoryLive && directory.data?.rfm_recommendations) {
      const recs = directory.data.rfm_recommendations;
      const m = getMethodology(methodologyId);
      return recs.map((r) => {
        const seg = SEGMENTS.find((s) => s.id === r.segment) || {};
        return {
          segmentId:    r.segment,
          segmentLabel: seg.label || r.segment,
          color:        seg.color || "var(--brown)",
          bg:           `${seg.color || "#0EA5E9"}12`,
          icon:         (r.title || "").split(" ")[0] || "🎯",
          title:        r.title,
          body:         r.rationale,
          count:        r.audience_size,
          priority:     r.priority,
          _source:      r.source,          // "pos_sales" — used by guard
          _actionable:  r.actionable,      // caller MAY attach action
        };
      }).filter(qw => qw.count > 0);      // no cards for 0-audience segments
    }
    if (!customers?.length) return [];
    const m = getMethodology(methodologyId);
    return buildQuickWins(SEGMENTS, customers, m.classify);
  }, [methodologyId, customers, SEGMENTS, isDirectoryLive, directory]);

  // Reset active segment when methodology changes
  const switchMethodology = useCallback((newId) => {
    setMethodologyId(newId);
    setActiveSegment("all");
    setSelected(null);
    localStorage.setItem("cust_method", newId);
  }, []);

  // Build a phone → list of campaigns that targeted this customer
  const campaignsByPhone = useMemo(() => {
    const m = new Map();
    for (const c of campaigns) {
      const ph = (c.targeted_phones || []);
      for (const p of ph) {
        if (!m.has(p)) m.set(p, []);
        m.get(p).push(c);
      }
    }
    return m;
  }, [campaigns]);

  const filtered = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.id === activeSegment) || SEGMENTS[0];
    const q = search.trim().toLowerCase();
    // v200.47 — when the live directory is loaded, filter by the
    // server-assigned segment code (`c.segment`) rather than re-running
    // the local classifier. Preserves lock-step with the segment strip.
    let list = customers.filter((c) => {
      if (seg.id === "all") return true;
      if (isDirectoryLive && c.segment) return c.segment === seg.id;
      return seg.filter(c);
    });
    if (q) {
      list = list.filter((c) =>
        (c.name || "").toLowerCase().includes(q) || (c.phone || "").includes(q)
      );
    }
    // Sort: at-risk/lost first (من c.segment مباشرة — بلا عتبات محلية)، ثم بالإنفاق، ثم الزيارات
    return list.sort((a, b) => {
      const aP = (a.segment === "at_risk" ? 2 : 0) + (a.segment === "lost" ? 1 : 0);
      const bP = (b.segment === "at_risk" ? 2 : 0) + (b.segment === "lost" ? 1 : 0);
      if (aP !== bP) return bP - aP;
      return (b.totalSpent || 0) - (a.totalSpent || 0) || (b.visits || 0) - (a.visits || 0);
    });
  }, [customers, activeSegment, search, SEGMENTS, isDirectoryLive]);

  // v200.47 — segCounts sourced from LIVE histogram, not from re-running
  // the local classifier. This ensures the strip and the RFM cards agree
  // by construction. "all" is the sum of all live rows.
  const segCounts = useMemo(() => {
    const counts = {};
    if (isDirectoryLive && directory.data?.segment_histogram) {
      const h = directory.data.segment_histogram;
      const total = Object.values(h).reduce((a, b) => a + (Number(b) || 0), 0);
      counts["all"] = total;
      for (const s of SEGMENTS) {
        if (s.id === "all") continue;
        counts[s.id] = Number(h[s.id]) || 0;
      }
      return counts;
    }
    // Fallback to local computation while the endpoint loads.
    for (const s of SEGMENTS) counts[s.id] = customers.filter(s.filter).length;
    return counts;
  }, [customers, SEGMENTS, isDirectoryLive, directory]);

  // ⭐ Dynamic segment snapshot — recomputes whenever activeSegment changes
  const segmentKpis = useMemo(() => {
    const seg = SEGMENTS.find((s) => s.id === activeSegment) || SEGMENTS[0];
    const list = customers.filter(seg.filter);
    const n = list.length;
    const avgVisits = n > 0 ? list.reduce((s, c) => s + (c.visits || 0), 0) / n : 0;
    const totalSpent = list.reduce((s, c) => s + (c.totalSpent || 0), 0);
    const avgSpend = n > 0 ? totalSpent / n : 0;
    const totalVisits = list.reduce((s, c) => s + (c.paid || 0) + (c.free || 0), 0);
    const avgSpendPerVisit = totalVisits > 0 ? totalSpent / totalVisits : 0;
    const topSpender = [...list].sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0))[0];
    return { seg, n, avgVisits, totalSpent, avgSpend, avgSpendPerVisit, topSpender };
  }, [customers, activeSegment, SEGMENTS]);

  // 🧠 AI Coach integration — fetch latest weekly movement report (RFM-style)
  const [coachData, setCoachData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/coach/latest").then(({ data }) => {
      if (alive && data?.report) setCoachData(data.report);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // v200.37 — server-computed segment stats (A1). ONE endpoint per Q1.
  // Card renders "N · مستبعَد M" per spec item 5. On failure or no
  // source we render "لا مصدر" (no zero fallback per spec item 6).
  const [segStats, setSegStats] = useState({ status: "loading" });
  // Phase 7d: لقطة الشرائح (segment-stats) تدعم الفرع فعليًا بالخلفية
  // (core.pos_data.fetch_segment_stats يفلتر branch_id) — موصولة بفلتر
  // الفرع من الشريط المشترك. «كل الفروع» = لا إرسال branch_id.
  // (segShared/segBranch مُعرَّفان أعلى الملف — يُستخدمان أيضًا لقائمة الدليل.)
  useEffect(() => {
    let alive = true;
    const params = { window: 90 };
    if (segBranch) params.branch_id = segBranch;
    api.get("/customers/segment-stats", { params })
      .then(({ data }) => {
        if (!alive) return;
        setSegStats({ status: "ok", data });
      })
      .catch(() => alive && setSegStats({ status: "no_source" }));
    return () => { alive = false; };
  }, [segBranch]);

  const serverSegmentCard = useMemo(() => {
    if (segStats.status !== "ok") return null;
    const seg = segStats.data?.pos_sales_stack?.segments?.[activeSegment];
    if (!seg) return null;
    return {
      count: seg.count || 0,
      audience_size: seg.audience_size || 0,
      excluded: segStats.data?.pos_sales_stack?.excluded_invalid || 0,
      total_spent: seg.total_spent || 0,
      total_visits: seg.total_visits || 0,
      avg_spent: seg.avg_spent_per_customer || 0,
      avg_visits: seg.avg_visits_per_customer || 0,
      top: seg.top_spender || null,
      window: segStats.data?.window || "90d",
      mode: segStats.data?.mode || "paid_only",
      coverage: segStats.data?.coverage || null,
    };
  }, [segStats, activeSegment]);

  return (
    <div data-testid="customers-page-wrap">
    {/* Phase 7f — SaudiMarketInsights تُرفع لطبقة «نظرة عامة على القاعدة»
         أعلى التبويب عند showMarketInsights=false (بلا حذف/تغيير منطق). */}
    {showMarketInsights && <SaudiMarketInsights />}
    <SegmentsLoadGate label="شرائح العملاء">
    {showRecommender && (
    <AICampaignRecommender
      onLaunch={(rec) => {
        // v120 white-screen fix: blastSegment must stay a STRING (segment id).
        // Passing an object made React render an object child → crash.
        const seg = SEGMENTS.find((s) => s.id === rec.segment_id);
        setBlastRec(rec);
        setBlastSegment(seg ? seg.id : (rec.segment_id || "all"));
      }}
    />
    )}
    <div className="card" data-testid="customers-smart" style={{ marginBottom: 14, padding: 0, overflow: "hidden" }}>
      {/* Header / command center */}
      <div style={{
        padding: "16px 20px",
        background: "linear-gradient(135deg, rgba(107,78,61,0.06) 0%, rgba(194,169,127,0.04) 100%)",
        borderBottom: "1px solid var(--border)",
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <h3 style={{ margin: 0, color: "var(--brown-deep)", fontSize: 22, fontWeight: 800, letterSpacing: -0.3 }}>
              👥 دليل العملاء الذكي
            </h3>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--muted)" }}>
              تواصل فوري عبر واتساب · تاريخ كامل لكل عميل · ربط بالحملات
            </p>
            {/* v200.47 (2026-08-13) — القائمة الآن حيّة من pos_sales
                (/api/customers/directory). الوسم الأصفر أدناه للأرشيف
                فقط، للعرض بلا زر تنفيذ — قاعدة دائمة (٥): أي رقم
                مصدره الأرشيف لا يحمل زر إرسال إطلاقاً. */}
            {isDirectoryLive && (
              <div data-testid="customers-live-tag" style={{
                marginTop: 6, display: "inline-block",
                padding: "3px 10px", borderRadius: 6,
                background: "rgba(16,185,129,0.10)", color: "#047857",
                fontSize: 10.5, fontWeight: 700,
              }}>
                ✓ حيّ — pos_sales · {fm(customers.length)} عميل نشط ·
                &nbsp;قاعدة كاملة {fm(directory.data?.total_base ?? customers.length)} ·
                &nbsp;نافذة {directory.data?.window_days || 90}ي · وضع {directory.data?.mode || "live"}
                {segBranch && <span>&nbsp;· فرع مُحدَّد</span>}
                {directory.data?.coverage && (
                  <span>&nbsp;· تغطية {fm(directory.data.coverage.with_key)}/{fm(directory.data.coverage.total_rows)}</span>
                )}
              </div>
            )}
            {/* v200.49 (2026-08-13) — archive tag REMOVED: this screen
                 draws every number from /api/customers/directory (all
                 pos_sales). Advertising an archive source that feeds
                 nothing was noise. */}
            {directory.data?.consistency_meta?.excluded_free_visits != null && (
              <div data-testid="customers-excluded-free-visits-line" style={{
                marginTop: 6, fontSize: 12, color: "#92400E", fontWeight: 600,
              }}>
                استُبعدت {fm(directory.data.consistency_meta.excluded_free_visits)} زيارة مجانية/مخصومة بالكامل في الفترة
              </div>
            )}
            {directory.status === "loading" && (
              <div data-testid="customers-loading-tag" style={{
                marginTop: 6, display: "inline-block",
                padding: "3px 10px", borderRadius: 6,
                background: "rgba(107,114,128,0.10)", color: "#6B7280",
                fontSize: 10.5, fontWeight: 700,
              }}>
                ⏳ جارٍ التحميل من pos_sales...
              </div>
            )}
            {directory.status === "error" && (
              <div data-testid="customers-error-tag" style={{
                marginTop: 6, display: "inline-block",
                padding: "3px 10px", borderRadius: 6,
                background: "rgba(220,38,38,0.10)", color: "#B91C1C",
                fontSize: 10.5, fontWeight: 700,
              }}>
                ⚠️ تعذّر الاتصال بـ pos_sales — لا يُعرض صفر (القاعدة ٦ من 2026-02-11)
              </div>
            )}
          </div>
          <input
            type="search"
            data-testid="customers-global-search-input"
            placeholder="🔍 ابحث باسم أو جوال..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              minWidth: 240,
              padding: "10px 14px",
              border: "1px solid var(--border)",
              borderRadius: 30,
              fontSize: 13,
              background: "#fff",
              outline: "none",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          />
        </div>

        {/* Methodology selector */}
        <div data-testid="methodology-selector" style={{
          marginTop: 12, padding: "10px 12px",
          background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: 10, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10,
        }}>
          <span style={{ fontSize: 11, color: "var(--muted)", fontWeight: 700 }}>📊 طريقة التصنيف:</span>
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
            {METHODOLOGIES.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => switchMethodology(m.id)}
                data-testid={`method-${m.id}`}
                style={{
                  padding: "5px 12px",
                  border: methodologyId === m.id ? "2px solid #6366F1" : "1px solid var(--border)",
                  background: methodologyId === m.id ? "#6366F1" : "#fff",
                  color: methodologyId === m.id ? "#fff" : "var(--brown-deep)",
                  borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                  transition: "all 160ms ease",
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <span style={{ fontSize: 11, color: "var(--muted)", flex: 1, minWidth: 200, textAlign: "end", lineHeight: 1.6 }}>
            {activeMethod.desc}
          </span>
        </div>

        {/* ⭐ SMART QUICK-WINS PANEL (RFM only) — top 3 actions sorted by priority */}
        {quickWins.length > 0 && (
          <div data-testid="rfm-quick-wins" style={{
            marginTop: 12, padding: "12px 14px",
            background: "linear-gradient(135deg, rgba(139,92,246,0.06) 0%, rgba(99,102,241,0.04) 100%)",
            border: "1px solid rgba(139,92,246,0.25)", borderRadius: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 16 }}>🧠</span>
                <b style={{ fontSize: 13, color: "var(--brown-deep)" }}>توصيات ذكية مبنية على RFM</b>
                <span style={{ fontSize: 10, color: "var(--muted)" }}>· مرتّبة حسب الأولوية</span>
              </div>
              <span style={{ fontSize: 10.5, color: "var(--muted)" }}>اضغط أي بطاقة للوصول للشريحة فوراً</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
              {quickWins.slice(0, 3).map((qw) => {
                const meta = PRIORITY_META[qw.priority];
                return (
                  <button
                    key={qw.segmentId}
                    type="button"
                    onClick={() => setActiveSegment(qw.segmentId)}
                    data-testid={`quick-win-${qw.segmentId}`}
                    style={{
                      padding: 12, borderRadius: 10, cursor: "pointer", textAlign: "start",
                      background: qw.bg, border: `1.5px solid ${qw.color}40`,
                      transition: "all 180ms ease", color: "var(--brown-deep)",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 20px -10px ${qw.color}50`; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontSize: 18 }}>{qw.icon}</span>
                      <span style={{
                        fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                        background: meta.color, color: "#fff", letterSpacing: 0.3,
                      }}>{meta.label}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: qw.color, marginBottom: 4 }}>
                      {qw.title}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--brown-deep)", lineHeight: 1.6, opacity: 0.85, marginBottom: 6 }}>
                      {qw.body}
                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--muted)", borderTop: "1px dashed var(--border)", paddingTop: 6, marginTop: 4 }}>
                      <b style={{ color: qw.color }}>{qw.count.toLocaleString("en-US")} عميل</b> في «{qw.segmentLabel}»
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ⭐ DYNAMIC SEGMENT SNAPSHOT — v200.37: server-computed via
             /api/customers/segment-stats. Renders "N · مستبعَد M" per
             spec item 5. On failure shows "لا مصدر" per item 6. */}
        <div data-testid="segment-snapshot" style={{
          marginTop: 12, padding: "10px 14px",
          background: "linear-gradient(135deg, var(--beige) 0%, rgba(245,237,224,0.4) 100%)",
          border: "1px solid var(--border)", borderRadius: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8, flexWrap: "wrap", gap: 6 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, color: "var(--muted)", fontWeight: 600 }}>📌 إحصائيات الشريحة الحالية:</span>
              <b style={{ fontSize: 13, color: "var(--brown-deep)" }}>{segmentKpis.seg.label}</b>
              {serverSegmentCard && (
                <span data-testid="server-envelope" style={{ fontSize: 10, color: "var(--muted)", background: "rgba(15,118,110,0.10)", padding: "2px 6px", borderRadius: 4 }}>
                  {serverSegmentCard.window} · {serverSegmentCard.mode}
                </span>
              )}
            </div>
            {serverSegmentCard?.top && (
              <span style={{ fontSize: 11, color: "var(--muted)" }}>
                🏆 الأعلى إنفاقاً: <b style={{ color: "var(--brown-deep)" }}>{serverSegmentCard.top.display_name}</b> ({Math.round(serverSegmentCard.top.spent).toLocaleString("en-US")} ر)
              </span>
            )}
          </div>
          {segStats.status === "no_source" && (
            <div data-testid="segment-snapshot-no-source" style={{ padding: "8px 10px", background: "rgba(217,119,6,0.10)", borderRadius: 6, color: "#B45309", fontSize: 12, fontWeight: 700 }}>
              ⚠️ لا مصدر — تعذَّر جلب إحصائيات الشرائح من الخادم. لا يُعرض صفر.
            </div>
          )}
          {segStats.status === "ok" && serverSegmentCard && (
            <>
              <div data-testid="segment-audience-line" style={{ fontSize: 11.5, color: "var(--brown-deep)", marginBottom: 6, fontWeight: 600 }}>
                👥 الجمهور <b>{serverSegmentCard.audience_size.toLocaleString("en-US")}</b>
                <span style={{ color: "var(--muted)", fontWeight: 500 }}> · مستبعَد </span>
                <b>{serverSegmentCard.excluded.toLocaleString("en-US")}</b>
                {/* v200.39 (بند ٤) — تفصيل الاستبعاد */}
                {(() => {
                  const br = segStats.data?.pos_sales_stack?.segments
                    ? (segStats.data?.excluded_breakdown ||
                        segStats.data?.pos_sales_stack?.excluded_breakdown ||
                        segStats.data?.coverage?.unattributed_breakdown ||
                        {})
                    : {};
                  const total = br.total || serverSegmentCard.excluded || 0;
                  if (total === 0) {
                    return (
                      <span data-testid="excluded-zero-proof" style={{ color: "var(--muted)", fontWeight: 500, marginInlineStart: 6 }}>
                        (مُثبت — لا صفوف بلا مفتاح)
                      </span>
                    );
                  }
                  return (
                    <span data-testid="excluded-breakdown" style={{ color: "var(--muted)", fontWeight: 500, marginInlineStart: 6 }}>
                      (بلا رقم {(br.no_phone || 0).toLocaleString("en-US")} · اشتقاق مفتاح فشل {(br.key_derivation_failed || 0).toLocaleString("en-US")})
                    </span>
                  );
                })()}
                {/* v200.39 (بند ٥) — «غير منسوب» ظاهر لا صفر مقنَّع */}
                {(() => {
                  const cov = serverSegmentCard.coverage || {};
                  const br = cov.unattributed_breakdown || {};
                  const total = br.total || 0;
                  if (total <= 0) return null;
                  return (
                    <span data-testid="segment-unattributed" style={{ marginInlineStart: 10, background: "rgba(217,119,6,0.10)", color: "#B45309", padding: "1px 8px", borderRadius: 6, fontWeight: 700 }}>
                      ⚠ غير منسوب {total.toLocaleString("en-US")}
                    </span>
                  );
                })()}
                {serverSegmentCard.coverage?.oldest_created_at_local && (
                  <span style={{ color: "var(--muted)", fontWeight: 500, marginInlineStart: 8 }}>
                    · التغطية منذ {String(serverSegmentCard.coverage.oldest_created_at_local).slice(0, 10)}
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 8 }}>
                <SnapshotCell label="عدد العملاء" value={serverSegmentCard.count.toLocaleString("en-US")} icon="👥"
                  testId="directory-segment-count-open" onClick={() => setDirListModal({ code: activeSegment })} />
                <SnapshotCell label="متوسط الزيارات" value={serverSegmentCard.avg_visits.toFixed(1)} icon="📊" sub="زيارة/عميل" />
                {spendingEnabled ? (
                  <>
                    <SnapshotCell label="متوسط الإنفاق" value={Math.round(serverSegmentCard.avg_spent).toLocaleString("en-US") + " ر"} icon="🧾" sub="ر/عميل" />
                    <SnapshotCell label="إجمالي الإنفاق" value={Math.round(serverSegmentCard.total_spent).toLocaleString("en-US") + " ر"} icon="💰" sub={`${serverSegmentCard.total_visits.toLocaleString("en-US")} زيارة`} />
                  </>
                ) : (
                  <>
                    <SnapshotCell label="نشطون" value={customers.filter((c) => (c.visits || 0) > 0 && segmentKpis.seg.filter(c)).length.toLocaleString("en-US")} icon="✅" />
                    <SnapshotCell label="غائبون" value={customers.filter((c) => (c.visits || 0) === 0 && segmentKpis.seg.filter(c)).length.toLocaleString("en-US")} icon="💤" />
                  </>
                )}
              </div>
            </>
          )}
          {segStats.status === "loading" && (
            <div data-testid="segment-snapshot-loading" style={{ fontSize: 12, color: "var(--muted)" }}>
              جاري تحميل إحصائيات الشرائح من الخادم…
            </div>
          )}
        </div>

        {/* 🧠 AI COACH WEEKLY MOVEMENT BANNER */}
        {coachData?.movements && Array.isArray(coachData.movements) && coachData.movements.length > 0 && (
          <div data-testid="coach-movements" style={{
            marginTop: 10, padding: "10px 14px",
            background: "linear-gradient(135deg, rgba(20,184,166,0.06) 0%, rgba(45,212,191,0.04) 100%)",
            border: "1px solid rgba(20,184,166,0.25)", borderRadius: 10,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 14 }}>🧠</span>
              <b style={{ fontSize: 12.5, color: "#0F766E" }}>تحرّكات الشرائح هذا الأسبوع (AI Coach)</b>
              {coachData.generated_at && (
                <span style={{ fontSize: 10, color: "var(--muted)" }}>· {String(coachData.generated_at).slice(0, 10)}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {coachData.movements.slice(0, 6).map((mv, i) => {
                const delta = (mv.this_week ?? 0) - (mv.last_week ?? 0);
                const isUp = delta > 0;
                const color = delta === 0 ? "#9CA3AF" : isUp ? "#10B981" : "#EF4444";
                return (
                  <div key={`${mv.segment}-${i}`} style={{
                    padding: "6px 12px", background: "var(--card)", borderRadius: 8,
                    border: "1px solid var(--border)", display: "inline-flex", alignItems: "center", gap: 6,
                  }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--brown-deep)" }}>{mv.label || mv.segment}</span>
                    <span style={{ fontSize: 12, fontWeight: 800, color }}>
                      {isUp ? "▲" : delta < 0 ? "▼" : "→"} {Math.abs(delta).toLocaleString("en-US")}
                    </span>
                    <span style={{ fontSize: 10, color: "var(--muted)" }}>
                      ({(mv.this_week ?? 0).toLocaleString("en-US")} الآن)
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Segment pills + Blast launcher */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 14 }}>
          <div data-testid="customers-segment-pills" style={{
            display: "flex",
            gap: 6,
            flex: 1,
            overflowX: "auto",
            paddingBottom: 4,
          }}>
            {SEGMENTS.map((s) => {
              const isActive = activeSegment === s.id;
              const count = segCounts[s.id] || 0;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setActiveSegment(s.id)}
                  data-testid={`segment-tab-${s.id}`}
 
                  style={{
                    padding: "7px 14px",
                    borderRadius: 999,
                    border: `1.5px solid ${isActive ? s.color : "transparent"}`,
                    background: isActive ? s.color : "transparent",
                    color: isActive ? "#fff" : s.color,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all 180ms ease",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                 aria-label={s.desc || ""}>
                  {s.label}
                  <span style={{
                    background: isActive ? "rgba(255,255,255,0.25)" : `${s.color}22`,
                    padding: "1px 7px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 800,
                  }}>
                    {fm(count)}
                  </span>
                </button>
              );
            })}
          </div>
          {/* Launch retention campaign on the current segment */}
          {activeSegment !== "all" && (segCounts[activeSegment] || 0) > 0 && (
            <button
              type="button"
              onClick={() => setBlastSegment(activeSegment)}
              data-testid="launch-blast-btn"
              style={{
                padding: "8px 16px",
                background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 800,
                cursor: "pointer",
                whiteSpace: "nowrap",
                boxShadow: "0 2px 8px rgba(37,211,102,0.35)",
                transition: "transform 160ms ease",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.05)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
             aria-label={`أطلق حملة استرداد على ${fm(segCounts[activeSegment])} عميل`}>
              
              🚀 أطلق حملة استرداد
            </button>
          )}
        </div>

        {/* Active segment description (helps user understand what they're seeing) */}
        {activeSegment !== "all" && (() => {
          const s = SEGMENTS.find((x) => x.id === activeSegment);
          if (!s || !s.desc) return null;
          return (
            <div data-testid="active-segment-desc" style={{
              marginTop: 10, padding: "8px 14px",
              background: `${s.color}10`, borderInlineStart: `3px solid ${s.color}`, borderRadius: 8,
              fontSize: 12, color: "var(--brown-deep)", lineHeight: 1.7,
            }}>
              <b style={{ color: s.color }}>{s.label}:</b> {s.desc}
            </div>
          );
        })()}
      </div>

      {/* Two-pane: list (right in RTL) + insights (left in RTL) */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) 320px",
        gap: 0,
      }} className="customers-smart-grid">
        {/* Customer list */}
        <div style={{ padding: 14, maxHeight: 640, overflowY: "auto" }} data-testid="customers-smart-list">
          {filtered.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "var(--muted)" }}>
              لا يوجد عملاء في هذه الشريحة {search && "بهذا البحث"}
            </div>
          ) : filtered.slice(0, 200).map((c) => {
            const badge = statusBadge(c);
            const isSelected = selected?.phone === c.phone;
            const camps = campaignsByPhone.get(normPhone(c.phone)) || [];
            return (
              <div
                key={c.phone + c.name}
                data-testid={`customer-row-${c.phone}`}
                onClick={() => setSelected(c)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 14px",
                  marginBottom: 8,
                  borderRadius: 12,
                  background: "#fff",
                  border: `1.5px solid ${isSelected ? "var(--gold)" : "var(--border)"}`,
                  cursor: "pointer",
                  transition: "all 160ms ease",
                  position: "relative",
                  boxShadow: isSelected ? "0 4px 12px rgba(194,169,127,0.25)" : "none",
                  transform: isSelected ? "translateX(-2px)" : "none",
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.borderColor = "var(--gold)"; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.borderColor = "var(--border)"; }}
              >
                {/* Avatar */}
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: avatarColor(c.name),
                  color: "#fff", fontSize: 18, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {avatarLetter(c.name)}
                </div>

                {/* Name + phone */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--brown-deep)", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                    <button type="button" data-testid={`customer-name-profile-${c.phone}`}
                      onClick={(e) => { e.stopPropagation(); setProfilePhoneKey(c.phone_key || c.phone); }}
                      style={{ background: "none", border: "none", padding: 0, font: "inherit",
                               color: "inherit", cursor: "pointer", textDecoration: "underline",
                               textDecorationColor: "transparent" }}
                      onMouseEnter={(e) => { e.currentTarget.style.textDecorationColor = "var(--gold)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.textDecorationColor = "transparent"; }}>
                      {c.name || "(بدون اسم)"}
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2, display: "flex", gap: 8, alignItems: "center" }}>
                    <span dir="ltr">{c.phone_display || c.phone || "—"}</span>
                    <span style={{
                      background: `${badge.color}1F`, color: badge.color,
                      padding: "1px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
                    }}>
                      {badge.lbl}
                    </span>
                  </div>
                </div>

                {/* Stats */}
                <div style={{ textAlign: "center", minWidth: 50 }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "var(--brown-deep)", lineHeight: 1 }}>
                    {fm(c.visits)}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 2 }}>زيارة · العمر</div>
                </div>

                {spendingEnabled && (c.totalSpent || 0) > 0 && (
                  <div style={{ textAlign: "center", minWidth: 60 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "var(--success)", lineHeight: 1 }}>
                      {fm(c.totalSpent)}
                    </div>
                    <div style={{ fontSize: 9, color: "var(--muted)", marginTop: 2 }}>ر إجمالي · العمر</div>
                  </div>
                )}

                <div style={{ textAlign: "center", minWidth: 70 }}>
                  <div style={{ fontSize: 11, color: "var(--brown)", fontWeight: 600 }}>
                    {relativeAr(c.lastDate)}
                  </div>
                  <div style={{ fontSize: 9, color: "var(--muted)" }}>آخر زيارة</div>
                </div>

                {/* WhatsApp action */}
                <div style={{ position: "relative", flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    data-testid={`whatsapp-action-${c.phone}`}
                    onClick={() => setWaOpenFor(waOpenFor === c.phone ? null : c.phone)}
                    disabled={!c.phone}
                    style={{
                      background: "rgba(37,211,102,0.12)",
                      color: "#128C7E",
                      border: "none",
                      padding: "8px 14px",
                      borderRadius: 999,
                      cursor: c.phone ? "pointer" : "not-allowed",
                      fontSize: 12,
                      fontWeight: 700,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      transition: "all 160ms ease",
                    }}
                    onMouseEnter={(e) => { if (c.phone) { e.currentTarget.style.background = "#25D366"; e.currentTarget.style.color = "#fff"; } }}
                    onMouseLeave={(e) => { if (c.phone) { e.currentTarget.style.background = "rgba(37,211,102,0.12)"; e.currentTarget.style.color = "#128C7E"; } }}
                  >
                    💬 واتساب
                  </button>
                  {waOpenFor === c.phone && (
                    <div style={{
                      position: "absolute",
                      top: "110%",
                      insetInlineEnd: 0,
                      background: "#fff",
                      border: "1px solid var(--border)",
                      borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      zIndex: zVar('extra_10'),
                      minWidth: 240,
                      padding: 6,
                    }} data-testid={`whatsapp-templates-${c.phone}`}>
                      {WA_TEMPLATES.map((t) => {
                        return (
                          <button
                            key={t.key}
                            type="button"
                            onClick={() => {
                              setWaOpenFor(null);
                              startServiceConversation({
                                phone: c.phone, source: `customers_smart:${t.key}`,
                              });
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              textAlign: "start",
                              padding: "8px 12px",
                              fontSize: 12.5,
                              color: "var(--brown-deep)",
                              background: "transparent",
                              border: "none",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontFamily: "inherit",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "var(--beige)"}
                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                          >
                            {t.label}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={() => {
                          setWaOpenFor(null);
                          startServiceConversation({
                            phone: c.phone, source: "customers_smart:manual",
                          });
                        }}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "start",
                          padding: "8px 12px",
                          fontSize: 12.5,
                          color: "var(--muted)",
                          background: "transparent",
                          border: "none",
                          borderTop: "1px dashed var(--border)",
                          marginTop: 4,
                          cursor: "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        ✏ محادثة جديدة بالرقم
                      </button>
                    </div>
                  )}
                </div>

                {/* v119.204 — Smart buttons: schedule + automate */}
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}
                     onClick={(e) => e.stopPropagation()}>
                  <button type="button"
                    data-testid={`schedule-action-${c.phone}`}
                    disabled={!c.phone}
                    onClick={() => setSmartAction({ customer: c, mode: "schedule" })}
                    style={{ background: "rgba(10,132,255,0.1)", color: "#0A84FF", border: "none",
                             padding: "4px 10px", borderRadius: 999, cursor: c.phone ? "pointer" : "not-allowed",
                             fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                    ⏰ جدولة
                  </button>
                  <button type="button"
                    data-testid={`automate-action-${c.phone}`}
                    disabled={!c.phone}
                    onClick={() => setSmartAction({ customer: c, mode: "automate" })}
                    style={{ background: "rgba(175,82,222,0.1)", color: "#AF52DE", border: "none",
                             padding: "4px 10px", borderRadius: 999, cursor: c.phone ? "pointer" : "not-allowed",
                             fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>
                    ⚙️ أتمتة
                  </button>
                </div>

                {camps.length > 0 && (
                  <div style={{
                    position: "absolute",
                    top: 6, insetInlineStart: 6,
                    background: "var(--gold)",
                    color: "#fff",
                    fontSize: 9,
                    fontWeight: 800,
                    padding: "1px 6px",
                    borderRadius: 999,
 }} ><InfoTip text={`في ${camps.length} حملة`} />
                    🎯{camps.length}
                  </div>
                )}
              </div>
            );
          })}
          {filtered.length > 200 && (
            <div style={{ padding: 12, textAlign: "center", color: "var(--muted)", fontSize: 11 }}>
              عرض أول 200 — استخدم البحث لتضييق النتائج
            </div>
          )}
        </div>

        {/* Insights pane */}
        <CustomerInsightsPane
          customer={selected}
          campaigns={selected ? (campaignsByPhone.get(normPhone(selected.phone)) || []) : []}
          spendingEnabled={spendingEnabled}
        />
      </div>

      {smartAction && (
        <SmartActionModal
          customer={smartAction.customer}
          mode={smartAction.mode}
          onClose={() => setSmartAction(null)}
        />
      )}

      <style>{`
        @media (max-width: 900px) {
          .customers-smart-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Blast campaign modal */}
      {blastSegment && (
        <BlastCampaignModal
          segment={blastSegment}
          segmentCount={segCounts[blastSegment] || blastRec?.audience_size || 0}
          customerNames={customers.filter(SEGMENTS.find((s) => s.id === blastSegment)?.filter || (() => false)).slice(0, 3).map((c) => c.name)}
          initialMessage={blastRec?.whatsapp_template || ""}
          onClose={() => { setBlastSegment(null); setBlastRec(null); }}
        />
      )}
      {/* B1 (2026-10) — القائمة الموحّدة لـ«عدد العملاء» في لقطة الشريحة */}
      {dirListModal && (
        <CustomerListModal
          kind="segment"
          branchId={segBranch || null}
          segmentCode={dirListModal.code}
          windowDays={90}
          title={`${getSegmentMeta(dirListModal.code)?.label || dirListModal.code} — دليل العملاء`}
          onClose={() => setDirListModal(null)}
        />
      )}
      <CustomerCardModal client={null} phone={profilePhoneKey}
        open={!!profilePhoneKey} onClose={() => setProfilePhoneKey(null)} />
    </div>
    </SegmentsLoadGate>
    </div>
  );
}

// ─── Insights side-pane ────────────────────────────────────────────────────
function CustomerInsightsPane({ customer, campaigns, spendingEnabled }) {
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState("");

  const askAi = useCallback(async () => {
    if (!customer) return;
    setAiBusy(true); setAiErr(""); setAiText("");
    try {
      const { data } = await api.post("/ai/customer-insight", {
        name: customer.name,
        phone: customer.phone,
        visits: customer.visits,
        total_spent: customer.totalSpent || 0,
        last_visit: customer.lastDate || "",
        is59: !!customer.is59,
        is59x: !!customer.is59x,
        paid: customer.paid || 0,
        free: customer.free || 0,
        in_campaigns: (campaigns || []).map((c) => ({ name: c.name, type: c.type, end_date: c.end_date })),
      });
      setAiText(data.text || "");
    } catch (e) {
      setAiErr("لا يمكن الحصول على تحليل الآن — حاول لاحقاً");
    } finally { setAiBusy(false); }
  }, [customer, campaigns]);

  if (!customer) {
    return (
      <div data-testid="customer-ai-insights-panel" style={{
        background: "linear-gradient(180deg, rgba(107,78,61,0.03) 0%, rgba(194,169,127,0.05) 100%)",
        padding: 22,
        borderInlineStart: "1px solid var(--border)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
      }}>
        <div>
          <div style={{ fontSize: 36, opacity: 0.4 }}>👆</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 8, lineHeight: 1.7 }}>
            اختر عميلاً لعرض رحلته<br />ورؤى ذكية عنه
          </div>
        </div>
      </div>
    );
  }

  const badge = statusBadge(customer);
  const avgSpend = customer.visits > 0 ? (customer.totalSpent || 0) / customer.visits : 0;

  return (
    <div data-testid="customer-ai-insights-panel" style={{
      background: "#fff",
      borderInlineStart: "1px solid var(--border)",
      padding: 18,
      maxHeight: 640,
      overflowY: "auto",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", gap: 12, marginBottom: 14,
        paddingBottom: 14, borderBottom: "1px dashed var(--border)",
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: avatarColor(customer.name),
          color: "#fff", fontSize: 22, fontWeight: 700,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {avatarLetter(customer.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--brown-deep)" }}>{customer.name || "(بدون اسم)"}</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 2 }} dir="ltr">{customer.phone || "—"}</div>
          <span style={{
            display: "inline-block",
            background: `${badge.color}1F`, color: badge.color,
            padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700,
            marginTop: 4,
          }}>{badge.lbl}</span>
        </div>
      </div>

      {/* Metrics bento */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
        <Tile label="عدد الزيارات (طوال العمر)" value={fm(customer.visits)} />
        <Tile label="آخر زيارة" value={relativeAr(customer.lastDate)} small />
        {spendingEnabled && (customer.totalSpent || 0) > 0 && (
          <>
            <Tile label="إجمالي الإنفاق (طوال العمر)" value={`${fm(customer.totalSpent)} ر`} color="var(--success)" />
            <Tile label="متوسط/زيارة" value={`${avgSpend.toFixed(1)} ر`} color="var(--brown-deep)" />
          </>
        )}
        {(customer.paid > 0 || customer.free > 0) && (
          <>
            <Tile label="فواتير مدفوعة" value={fm(customer.paid || 0)} color="var(--success)" />
            <Tile label="أكواب مجانية" value={fm(customer.free || 0)} color="#D97706" />
          </>
        )}
      </div>

      {/* Journey timeline */}
      {campaigns.length > 0 && (
        <div data-testid="customer-journey-timeline" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--brown-deep)", marginBottom: 8 }}>
            🎯 الحملات التي استهدفت العميل ({campaigns.length})
          </div>
          {campaigns.map((c) => (
            <div key={c.id} style={{
              padding: 8, fontSize: 11.5, lineHeight: 1.6,
              background: "var(--beige)", borderInlineStart: "3px solid var(--gold)",
              borderRadius: 6, marginBottom: 6,
            }}>
              <b style={{ color: "var(--brown-deep)" }}>{c.name}</b>
              <div style={{ color: "var(--muted)", fontSize: 10 }}>
                {c.start_date} → {c.end_date} · {c.status === "active" ? "🟢 نشطة" : "⏹ منتهية"}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Insight CTA */}
      <button
        type="button"
        onClick={askAi}
        disabled={aiBusy}
        data-testid="customer-ai-ask"
        className="btn btn-primary"
        style={{ width: "100%", marginBottom: 10, fontSize: 12 }}
      >
        {aiBusy ? "⏳ يحلّل Claude..." : "🧠 تحليل ذكي للعميل"}
      </button>
      {aiErr && <div className="al alr" style={{ fontSize: 11 }}>{aiErr}</div>}
      {aiText && (
        <div style={{
          padding: 12,
          background: "linear-gradient(135deg, rgba(99,102,241,0.06) 0%, rgba(236,72,153,0.04) 100%)",
          borderInlineStart: "3px solid #6366F1",
          borderRadius: 6,
          fontSize: 12.5,
          lineHeight: 1.9,
          color: "var(--brown-deep)",
          whiteSpace: "pre-wrap",
        }} data-testid="customer-ai-text">
          {aiText}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, color = "var(--brown-deep)", small }) {
  return (
    <div style={{
      padding: "8px 10px",
      background: "var(--beige)",
      borderRadius: 6,
      textAlign: "center",
    }}>
      <div style={{ fontSize: small ? 12 : 18, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

// Compact cell used inside the dynamic segment snapshot panel
function SnapshotCell({ label, value, icon, sub, onClick, testId }) {
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: "8px 10px", background: "var(--card)", borderRadius: 8,
        border: "1px solid var(--border)", display: "flex", flexDirection: "column",
        alignItems: "center", textAlign: "center", gap: 2,
        transition: "transform 160ms ease, box-shadow 200ms ease",
        cursor: onClick ? "pointer" : "default",
      }}>
      <div style={{ fontSize: 14 }}>{icon}</div>
      <div style={{ fontSize: 16, fontWeight: 800, color: "var(--brown-deep)", lineHeight: 1.1, fontVariantNumeric: "tabular-nums", textDecoration: onClick ? "underline" : "none", textDecorationStyle: "dotted" }}>{value}</div>
      <div style={{ fontSize: 10, color: "var(--muted)", fontWeight: 600 }}>{label}</div>
      {sub && <div style={{ fontSize: 9.5, color: "var(--muted)", opacity: 0.8 }}>{sub}</div>}
    </div>
  );
}

// ─── v119.204 Smart Action Modal — ⏰ جدولة / ⚙️ أتمتة per customer ────────
// Creates a scheduled (once) or automated (recurring) WhatsApp campaign for a
// single customer via /api/scheduled-campaigns (segment=manual).
export function SmartActionModal({ customer, mode, onClose }) {
  const isAutomate = mode === "automate";
  const [message, setMessage] = useState(
    `مرحباً ${customer?.name || "{name}"}! وحشتنا في ثواني ☕ قهوتك المفضلة بانتظارك 💛`
  );
  const [hour, setHour] = useState(17);
  const [cadence, setCadence] = useState(isAutomate ? "weekly" : "once");
  const [weekday, setWeekday] = useState(4); // الخميس
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const phoneDigits = String(customer?.phone || "").replace(/[^\d]/g, "");

  const save = async () => {
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/scheduled-campaigns", {
        name: `${isAutomate ? "أتمتة" : "جدولة"} — ${customer?.name || customer?.phone}`,
        template: message.trim(),
        segment: "manual",
        manual_phones: [phoneDigits],
        cadence,
        weekday: cadence === "weekly" ? Number(weekday) : null,
        day_of_month: cadence === "monthly" ? Number(dayOfMonth) : null,
        hour: Number(hour),
        enabled: true,
      });
      setDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "فشل الحفظ — تأكد من صلاحياتك");
    }
    setBusy(false);
  };

  const WD = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

  // Portal → body: fixed positioning breaks inside transformed ancestors
  return createPortal(
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: zVar('extra_1000'),
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} data-testid="smart-action-modal">
      <div onClick={(e) => e.stopPropagation()} className="modal-content" style={{
        background: "#fff", borderRadius: 18, padding: 20, width: "100%", maxWidth: 440,
      }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: "var(--brown-deep)", marginBottom: 4 }}>
          {isAutomate ? "⚙️ أتمتة رسالة دورية" : "⏰ جدولة رسالة"} — {customer?.name || customer?.phone}
        </div>
        <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 12 }}>
          {isAutomate
            ? "رسالة WhatsApp تُرسل تلقائياً بشكل دوري لهذا العميل."
            : "رسالة WhatsApp تُرسل تلقائياً مرة واحدة في الوقت المحدد."}
        </div>
        {done ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 40 }}>✅</div>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--brown-deep)", marginTop: 8 }}>
              تم الحفظ! ستُرسل تلقائياً حسب الجدول — إدارتها من تبويب «جدولة الحملات».
            </div>
            <button className="btn btn-dark" onClick={onClose} data-testid="smart-action-done"
              style={{ marginTop: 14, fontSize: 13 }}>إغلاق</button>
          </div>
        ) : (
          <>
            <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>
              💬 نص الرسالة
            </label>
            <textarea rows={3} className="sim-input" value={message}
              onChange={(e) => setMessage(e.target.value)}
              data-testid="smart-action-message"
              style={{ width: "100%", marginBottom: 10, fontSize: 13 }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>
                  🕐 الساعة (KSA)
                </label>
                <input type="number" min="0" max="23" className="sim-input" value={hour}
                  onChange={(e) => setHour(e.target.value)} data-testid="smart-action-hour"
                  style={{ width: "100%" }} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>
                  🔁 التكرار
                </label>
                <select className="sim-input" value={cadence}
                  onChange={(e) => setCadence(e.target.value)} data-testid="smart-action-cadence"
                  style={{ width: "100%" }}>
                  {!isAutomate && <option value="once">مرة واحدة (غداً/أقرب موعد)</option>}
                  <option value="daily">يومياً</option>
                  <option value="weekly">أسبوعياً</option>
                  <option value="monthly">شهرياً</option>
                </select>
              </div>
            </div>
            {cadence === "weekly" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>
                  📅 يوم الأسبوع
                </label>
                <select className="sim-input" value={weekday}
                  onChange={(e) => setWeekday(e.target.value)} data-testid="smart-action-weekday"
                  style={{ width: "100%" }}>
                  {WD.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            {cadence === "monthly" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 11.5, fontWeight: 700, marginBottom: 4, color: "var(--muted)" }}>
                  📅 يوم من الشهر
                </label>
                <input type="number" min="1" max="31" className="sim-input" value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)} data-testid="smart-action-dom"
                  style={{ width: "100%" }} />
              </div>
            )}
            <div className="modal-actions-sticky" style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="btn btn-soft" onClick={onClose} style={{ fontSize: 13 }}>إلغاء</button>
              <button className="btn btn-dark" onClick={save} disabled={busy || !message.trim()}
                data-testid="smart-action-save" style={{ fontSize: 13 }}>
                {busy ? "⏳ يحفظ..." : isAutomate ? "⚙️ تفعيل الأتمتة" : "⏰ جدولة الإرسال"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
